<div class="stats-container">
  <mat-toolbar color="primary" class="stats-toolbar">
    <span>Solver Performance Metrics</span>
    <span class="spacer"></span>
    <button mat-button (click)="fetchMetrics()" [disabled]="loading">
      <mat-icon>refresh</mat-icon>
      Refresh
    </button>
    <button mat-button (click)="toggleAutoRefresh()">
      <mat-icon>{{ autoRefresh ? "pause" : "play_arrow" }}</mat-icon>
      {{ autoRefresh ? "Pause" : "Auto" }}
    </button>
  </mat-toolbar>

  @if (loading && !metrics) {
    <div class="loading-container">
      <mat-progress-spinner
        mode="indeterminate"
        diameter="48"
      ></mat-progress-spinner>
      <p>Loading solver metrics...</p>
    </div>
  }

  @if (error) {
    <div class="error-banner">
      <mat-icon>error_outline</mat-icon>
      <span>{{ error }}</span>
    </div>
  }

  @if (metrics) {
    <div class="metrics-grid">
      <mat-card class="metric-card">
        <mat-card-header>
          <mat-card-title>Solver Status</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <div class="status-badge" [class]="getStatusColor()">
            {{ metrics.solverStatus }}
          </div>
          <div class="detail-row">
            <span class="label">Uptime</span>
            <span class="value">{{ metrics.uptime }}</span>
          </div>
          <div class="detail-row">
            <span class="label">Solve Duration</span>
            <span class="value"
              >{{ metrics.solveDurationSeconds | number: "1.1-1" }}s</span
            >
          </div>
        </mat-card-content>
      </mat-card>

      <mat-card class="metric-card">
        <mat-card-header>
          <mat-card-title>Best Solutions</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <div class="big-number">{{ metrics.bestSolutionCount }}</div>
          <div class="detail-row">
            <span class="label">Last Improvement</span>
            <span class="value">
              @if (metrics.lastBestSolutionTime) {
                {{ metrics.lastBestSolutionTime | date: "HH:mm:ss" }}
              } @else {
                -
              }
            </span>
          </div>
        </mat-card-content>
      </mat-card>

      <mat-card class="metric-card">
        <mat-card-header>
          <mat-card-title>Memory</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <div class="memory-bar-container">
            <div class="memory-bar" [style.width.%]="getMemoryPercent()"></div>
          </div>
          <div class="memory-labels">
            <span>{{ formatBytes(metrics.memoryUsedBytes) }} used</span>
            <span>{{ formatBytes(metrics.memoryMaxBytes) }} max</span>
          </div>
          <div class="detail-row">
            <span class="label">Usage</span>
            <span class="value"
              >{{ getMemoryPercent() | number: "1.1-1" }}%</span
            >
          </div>
        </mat-card-content>
      </mat-card>

      <mat-card class="metric-card">
        <mat-card-header>
          <mat-card-title>Problem Size</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <div class="detail-row">
            <span class="label">Tickets</span>
            <span class="value big">{{ metrics.ticketCount | number }}</span>
          </div>
          <div class="detail-row">
            <span class="label">Resource Availabilities</span>
            <span class="value big">{{
              metrics.resourceAvailabilityCount | number
            }}</span>
          </div>
          <mat-divider></mat-divider>
          <div class="detail-row" style="margin-top: 12px">
            <span class="label">Partitioning</span>
            <span class="value">{{
              metrics.partitioningEnabled ? "Enabled" : "Disabled"
            }}</span>
          </div>
          @if (metrics.partitioningEnabled) {
            <div class="detail-row">
              <span class="label">Partitions</span>
              <span class="value">{{ metrics.partitionCount }}</span>
            </div>
          }
          @if (getCoreSizeStats().length > 0) {
            <mat-divider></mat-divider>
            <div class="core-stats-section">
              <div class="core-stats-header">Core Stats</div>
              <div class="core-stats-grid">
                @for (core of getCoreSizeStats(); track core.coreLabel) {
                  <div class="core-stat-row">
                    <span class="core-label">{{ core.coreLabel }}</span>
                    <span class="core-values"
                      >{{ core.tickets | number }}t ·
                      {{ core.resources | number }}r</span
                    >
                  </div>
                }
              </div>
            </div>
          }
        </mat-card-content>
      </mat-card>

      <mat-card
        class="metric-card chart-card"
        [class.maximized]="maximizedCard === 'speed'"
      >
        <mat-card-header>
          <mat-card-title>Partition Speed</mat-card-title>
          <span class="spacer"></span>
          <button mat-icon-button (click)="toggleMaximize('speed')">
            <mat-icon>{{
              maximizedCard === "speed" ? "close_fullscreen" : "open_in_full"
            }}</mat-icon>
          </button>
        </mat-card-header>
        <mat-card-content>
          <div class="chart-subtitle">
            <mat-icon>info</mat-icon>
            Stats since last solver restart
          </div>
          <div class="chart-time-controls">
            @for (opt of TIME_WINDOW_OPTIONS; track opt.minutes) {
              <button
                class="time-btn"
                [class.active]="chartTimeWindowMinutes === opt.minutes"
                (click)="setChartTimeWindow(opt.minutes)"
              >
                {{ opt.label }}
              </button>
            }
          </div>

          @if (hasSpeedChartData()) {
            <div
              echarts
              [options]="speedChartOptions"
              class="chart-host speed-host"
            ></div>
          } @else {
            <div class="empty-chart">
              Waiting for speed samples (recorded after solver progress).
            </div>
          }
        </mat-card-content>
      </mat-card>

      <mat-card
        class="metric-card chart-card"
        [class.maximized]="maximizedCard === 'heatmap'"
      >
        <mat-card-header>
          <mat-card-title>Core x Date Heatmap</mat-card-title>
          <span class="spacer"></span>
          <button mat-icon-button (click)="toggleMaximize('heatmap')">
            <mat-icon>{{
              maximizedCard === "heatmap" ? "close_fullscreen" : "open_in_full"
            }}</mat-icon>
          </button>
        </mat-card-header>
        <mat-card-content>
          @if (hasHeatmapData()) {
            <div
              echarts
              [options]="heatmapOptions"
              class="chart-host heatmap-host"
            ></div>
          } @else {
            <div class="empty-chart">
              Waiting for core/date partition stats from current run.
            </div>
          }
        </mat-card-content>
      </mat-card>
    </div>

    @if (maximizedCard) {
      <div class="max-backdrop" (click)="closeMaximize()"></div>
    }

    <div class="footer-info">
      Last refreshed: {{ lastRefreshed | date: "HH:mm:ss" }} · Auto-refresh:
      {{ autoRefresh ? "ON (" + refreshInterval / 1000 + "s)" : "OFF" }}
    </div>
  }
</div>
